import { Alert, Group, Stack, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useRef } from 'react';
import type { FC } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { VenueSelect } from '../../../components/form/VenueSelect.tsx';
import { DateTimePicker } from '../../../components/reactHookFormMantine/DateTimePicker.tsx';
import { SegmentedControl } from '../../../components/reactHookFormMantine/SegmentedControl.tsx';
import { Textarea } from '../../../components/reactHookFormMantine/Textarea.tsx';
import { TextInput } from '../../../components/reactHookFormMantine/TextInput.tsx';
import { useI18n } from '../../../hooks/useI18n.ts';
import type { EventFormComplete } from '../EventForm/schema.ts';

export const WhereAndWhenSingleDate: FC<{
  useFormValue: UseFormReturn<EventFormComplete>;
}> = ({ useFormValue: { control, watch, setValue, clearErrors } }) => {
  const { t } = useI18n();

  const activeEventType = watch('eventType');
  const previousEventType = useRef(activeEventType);
  const tomorrowTime = dayjs().add(1, 'day').set('hour', 15).set('minute', 0).set('second', 0).toDate();

  const start = watch('start');
  const end = watch('end');

  useEffect(() => {
    if (dayjs(end).isBefore(dayjs(start))) {
      setValue('end', dayjs(start).add(1, 'hours').toDate());
    }
  }, [start, end, setValue]);

  useEffect(() => {
    if (previousEventType.current === activeEventType) return;
    const prev = previousEventType.current;
    previousEventType.current = activeEventType;

    if (prev === 'online') {
      setValue('onlineLink', '');
      clearErrors('onlineLink');
    }
    if (prev === 'live') {
      setValue('venue', undefined);
      setValue('customVenue', '');
      clearErrors('venue');
    }
  }, [activeEventType, setValue, clearErrors]);

  return (
    <Stack gap="xs" justify="flex-start">
      <Title mt="lg" order={4}>
        {t('where-and-when_title--where') ?? 'Where'}
      </Title>
      <SegmentedControl
        control={control}
        data={[
          { value: 'live', label: t('where-and-when_segment--live') ?? 'Live' },
          {
            value: 'online',
            label: t('where-and-when_segment--online') ?? 'Online',
          },
          {
            value: 'secret',
            label: t('where-and-when_segment--secret') ?? 'Secret Location',
          },
        ]}
        name="eventType"
      />
      {activeEventType === 'live' && (
        <Stack>
          <VenueSelect control={control} label={t('where-and-when_label--venue') ?? 'Choose venue'} name="venue" />
          <Textarea
            autosize
            control={control}
            description={
              t('where-and-when_description--custom-venue') ??
              'If the venue is not listed in the dropdown above, please write the name of the venue here'
            }
            label={t('where-and-when_label--custom-venue') ?? 'Custom venue'}
            minRows={2}
            name="customVenue"
            placeholder={
              t('where-and-when_placeholder--custom-venue') ?? 'Enter venue name if not available in the dropdown'
            }
          />
        </Stack>
      )}

      {activeEventType === 'online' && (
        <TextInput
          control={control}
          label={t('where-and-when_label--online-link') ?? 'Online link'}
          name="onlineLink"
          placeholder={t('where-and-when_placeholder--online-link') ?? 'Link where the event will take place'}
          type="input"
        />
      )}
      {activeEventType === 'secret' && (
        <Alert color="gigi-aqua" p="xs" radius="xs">
          {t('where-and-when_alert--secret') ?? 'You can later make this event known'}
        </Alert>
      )}

      <Title mt="lg" order={4}>
        {t('where-and-when_title--when') ?? 'When'}
      </Title>
      <Group grow>
        <DateTimePicker
          required
          control={control}
          defaultValue={tomorrowTime}
          label={t('where-and-when_label--start') ?? 'Start'}
          minDate={tomorrowTime}
          name="start"
          valueFormat="dddd, D MMMM YYYY HH:mm"
        />

        <DateTimePicker
          required
          control={control}
          label={t('where-and-when_label--end') ?? 'End'}
          minDate={dayjs(start).add(1, 'hours').toDate()}
          name="end"
          valueFormat="dddd, D MMMM YYYY HH:mm"
        />
      </Group>
    </Stack>
  );
};
